import React from 'react';
import styled from 'styled-components';
import { ReactComponent as Background } from '../../assets/pro-plan-bg.svg';
import { ReactComponent as LogoIcon } from '../../assets/logo-gradient.svg';
import { ReactComponent as BestValueChip } from '../../assets/best-value-chip.svg';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography';
import { SpaceButton } from '../../components/space-button/space-button.component';
import {
    BillingLabel,
    BulletRow,
    FeaturesContainer,
    PerText,
    PlanWrapper,
    PricingText,
    PricingTextCrossed,
} from './account-settings.styles';
import { BillingCycleType } from '../../types/billing-cycle.type';
import { MONTHLY_PRO_PRICE_ID, YEARLY_PRO_PRICE_ID } from '../../constants';

export const ProPlanCard = ({
    active,
    onClick,
    loading,
    billingCycle,
}: {
    active?: boolean;
    loading: boolean;
    onClick: (priceId: string) => void;
    billingCycle: BillingCycleType;
}) => (
    <PlanWrapper>
        <PlanContainer>
            <BgContainer>
                <Background />
            </BgContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <LogoIcon />
                <BestValueChip />
            </Stack>
            <Stack gap={2}>
                <Typography sx={{ fontSize: '24px', fontWeight: 500, color: '#fff' }}>PRO Plan</Typography>
                <Typography sx={{ fontSize: '16px', color: '#71708C', height: '72px' }}>
                    Includes advanced features such as review generation, video reviews, and supports multiple platforms.
                </Typography>
            </Stack>
            <Stack gap={1}>
                <Stack direction="row" alignItems="flex-end" gap={0.5}>
                    {billingCycle === 'year' && <PricingTextCrossed>$ 59</PricingTextCrossed>}
                    <PricingText>{billingCycle === 'year' ? '$ 49' : '$ 59'}</PricingText>
                    <PerText>/month</PerText>
                </Stack>
                <BillingLabel>Billed {billingCycle}ly</BillingLabel>
            </Stack>
            <SpaceButton
                disabled={active || loading}
                variant="secondary"
                onClick={() => {
                    if (!active) {
                        onClick(billingCycle === 'year' ? YEARLY_PRO_PRICE_ID : MONTHLY_PRO_PRICE_ID);
                    }
                }}
            >
                {active ? <>Your current plan</> : <>Choose this plan</>}
            </SpaceButton>
        </PlanContainer>
        <FeaturesContainer>
            <BulletRow label="Advanced review form with negative feedback filtering" />
            <BulletRow label="In-form video review recording" />
            <BulletRow label="Manage and display video reviews in the widget" />
            <BulletRow label="Multi-step SMS and email review request campaigns" />
            <BulletRow label="Unlimited email review requests" />
            <BulletRow label="Comprehensive review request analytics" />
            <BulletRow label="Daily auto-import reviews" />
            <BulletRow label="Priority customer support" />
            <Stack width="fit-content" borderRadius={46} sx={{ padding: '4px 12px', background: '#F7F8FA' }}>
                <Typography fontSize="14px" fontWeight="500">
                    & ESSENTIAL FEATURES
                </Typography>
            </Stack>
        </FeaturesContainer>
    </PlanWrapper>
);

const PlanContainer = styled.div<{ $logoGrey?: boolean }>`
    position: relative;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    background: radial-gradient(
            359.3% 158.28% at 49.94% 6.72%,
            rgba(6, 7, 13, 0) 0%,
            rgba(103, 39, 255, 0.6) 49.92%,
            rgba(210, 190, 255, 0.6) 85.04%
        ),
        #101220;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 40px;
    overflow: hidden;
    height: fit-content;
    width: 100%;
    flex-shrink: 0;
`;

const BgContainer = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    pointer-events: none;
`;
